<template>
  <div class="discover-clubs">
    <div class="search-section">
      <el-input
        v-model="searchQuery"
        placeholder="Search clubs..."
        prefix-icon="el-icon-search"
        clearable
        @clear="handleSearchClear"
        @input="handleSearch"
      >
      </el-input>
    </div>

    <div class="clubs-grid" v-if="isAdvisor">
      <div v-for="club in filteredClubs" :key="club._id.$id" class="club-card">
        <div class="club-info">
          <div class="club-header">
            <img :src="club.avatar_address || defaultClubImage" alt="Club icon" class="club-icon">
            <h3>{{ club.name }}</h3>
          </div>
          <p class="club-description">{{ club.intro || 'No description available' }}</p>
          <div class="club-meta">
            <div class="club-source" v-if="club.club_source">
              <span class="source-value">{{ formatClubSource(club.club_source) }}</span>
            </div>
            <div class="type-tags" v-if="club.type_tags && club.type_tags.length">
              <el-tag
                v-for="tag in club.type_tags"
                :key="tag"
                size="small"
                type="info"
              >
                {{ tag }}
              </el-tag>
            </div>
            <!-- <span><i class="el-icon-date"></i> Created {{ formatDate(club.create_time) }}</span> -->
          </div>
          <div class="club-actions">
            <el-button 
              :type="isClubEstablished(club) ? 'info' : 'primary'"
              :plain="isClubEstablished(club)"
              size="small"
              @click="isClubEstablished(club) ? goToSchoolClub(club) : handleEstablishClub(club)"
            >
              {{ isClubEstablished(club) ? 'Go to our school club' : 'Establish one' }}
            </el-button>
            <el-button 
              type="text" 
              size="small"
              @click="viewClubDetail(club)"
            >
              View Details
            </el-button>
          </div>
        </div>
      </div>
    </div>

    

    <div v-if="!filteredClubs.length" class="no-results">
      <i class="el-icon-search"></i>
      <p>No clubs found</p>
    </div>
  </div>
</template>

<script>
import { postRequest } from '@/api/eclub'
import { getUserId, getUserType, getAccountId } from '@/utils/store'

export default {
  name: 'DiscoverClubs',
  
  data() {
    return {
      clubs: [],
      accountClubs: [],
      searchQuery: '',
      loading: false,
      currentUserId: getUserId(),
      joinedClubIds: new Set(),
      establishedClubIds: new Set(),
      isAdvisor: false,
      // Simple placeholder image as base64
      defaultClubImage: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAABkCAYAAABw4pVUAAAABmJLR0QA/wD/AP+gvaeTAAABKklEQVR4nO3ZMQrCQBRF0ZdYuI28K3IXbsNCrEQQbN2D2xAr0U6baCAJk8zknXPrYeAW82cKAAAAAAAAAAAAgK96jHwP+DvHJOcktyT7JNveOWM1S3JKck1yTrLpm/O7FklOSR5JlkluSXZ9k8ZlkeScz2tySXLomjQi8ySnfF+TY5Jt16qRWCY5Jrnn55qsO3aNwCrJIck9xWuyapk2cOsU12TVMm3A5im+JuvWcQO1SPGaLFvHDdA25TVZtM4boG3Ka7JoHdhlluScsoXTqwu7zJOcUr5werZMHIBdyhdOz9aJnW1SvnB6tk7sbJPyhVOzdeIAbFK+cGq2ThyATcoXTs3WiQOwSfnCqdk6cQA2KV84NVsnDsAm5QunZuvEAdikfOEAAAAAAAAAAAAAAADwz7wAQVU8HBnj1CkAAAAASUVORK5CYII='
    }
  },

  computed: {
    filteredClubs() {
      if (!this.searchQuery) return this.clubs
      const query = this.searchQuery.toLowerCase()
      return this.clubs.filter(club => 
        club.name.toLowerCase().includes(query) ||
        (club.description && club.description.toLowerCase().includes(query))
      )
    }
  },

  mounted() {
    this.isAdvisor = getUserType() === 'advisor'
    if (this.isAdvisor) {
      this.fetchStoreClubs()
    } else {
      this.fetchAccountClubs()
      this.fetchJoinedClubs()
    }
  },

  methods: {
    async fetchStoreClubs() {
      try {
        this.loading = true
        const response = await postRequest('getAllClubs', {
          user_id: this.currentUserId
        })
        
        if (response.data.code === 0) {
          this.clubs = response.data.data
        } else {
          this.$message.error('Failed to fetch clubs')
        }
      } catch (error) {
        console.error('Error fetching clubs:', error)
        this.$message.error('Failed to fetch clubs')
      } finally {
        this.loading = false
      }
    },

    async fetchAccountClubs() {
      try {
        this.loading = true
        const response = await postRequest('getSchoolAdminClubs', {
          creator_id: getAccountId()
        })
        
        if (response.data.code === 0) {
          this.accountClubs = response.data.data
        } else {
          this.$message.error('Failed to fetch clubs')
        }
      } catch (error) {
        console.error('Error fetching account clubs:', error)
        this.$message.error('Failed to fetch clubs')
      } finally {
        this.loading = false
      }
    },

    async fetchJoinedClubs() {
      try {
        const response = await postRequest('getStudentClubs', {
          user_id: this.currentUserId
        })
        
        if (response.data.code === 0) {
          this.joinedClubIds = new Set(response.data.data.map(club => club._id.$id))
        }
      } catch (error) {
        console.error('Error fetching joined clubs:', error)
      }
    },

    async handleJoinClub(club) {
      try {
        const response = await postRequest('joinClub', {
          club_id: club._id.$id,
          user_id: this.currentUserId
        })
        
        if (response.data.code === 0) {
          this.$message.success('Successfully joined the club')
          await this.fetchJoinedClubs()
        } else {
          this.$message.error(response.data.message || 'Failed to join club')
        }
      } catch (error) {
        console.error('Error joining club:', error)
        this.$message.error('Failed to join club')
      }
    },

    viewClubDetail(club) {
      this.$router.push({
        name: 'Club Profile',
        query: {
          club_id: club._id.$id
        }
      })
    },

    isClubEstablished(club) {
      return club.school_club_id && club.school_club_id !== ''
    },

    isClubMember(club) {
      return this.joinedClubIds.has(club._id.$id)
    },

    handleSearch() {
      // Implement debounced search if needed
    },

    handleSearchClear() {
      this.searchQuery = ''
    },

    formatDate(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp * 1000)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },

    formatClubSource(source) {
      const sourceMap = {
        'eae_original': 'EAE Original',
        'external_brand': 'External Branded',
        'common_genre': 'Common Genre'
      }
      return sourceMap[source] || source
    },

    async goToSchoolClub(club) {
      this.$router.push({
        name: 'Club Detail',
        query: {
          club_id: club.school_club_id
        }
      })
    },

    async handleEstablishClub(club) {
      try {
        // Show confirmation dialog
        await this.$confirm(
          `Are you sure you want to establish one "${club.name}" in your school?`,
          'Establish Club',
          {
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            type: 'info'
          }
        )

        // Make API request
        const response = await postRequest('establishStoreClubInSchool', {
          user_id: this.currentUserId,
          store_club_id: club._id.$id,
          user_type: 'advisor'
        })

        if (response.data.code === 0) {
          this.$message.success('Club established successfully')
          const new_school_club = response.data.data
          // Redirect to the new club's detail page
          this.$router.push({
            name: 'Club Detail',
            query: {
              club_id: new_school_club._id.$id
            }
          })
        } else {
          this.$message.error(response.data.message || 'Failed to establish club')
        }
      } catch (error) {
        if (error !== 'cancel') { // Ignore if user cancelled the dialog
          console.error('Error establishing club:', error)
          this.$message.error('Failed to establish club')
        }
      }
    },

  }
}
</script>

<style scoped lang="scss">
.discover-clubs {
  padding: 20px;

  .search-section {
    max-width: 600px;
    margin: 0 auto 30px;
  }

  .clubs-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
    padding: 20px;
  }

  .club-card {
    background: white;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-5px);
    }

    .club-info {
      padding: 20px;

      .club-header {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 16px;

        .club-icon {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          object-fit: cover;
        }

        h3 {
          margin: 0;
          font-size: 18px;
          color: #303133;
        }
      }

      .club-description {
        color: #606266;
        font-size: 14px;
        margin-bottom: 15px;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
        line-height: 1.4;
        height: calc(1.4em * 3);
      }

      .club-meta {
        display: flex;
        flex-direction: column;
        gap: 10px;
        color: #909399;
        font-size: 13px;
        margin-bottom: 15px;

        .club-source {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;

          .source-label {
            color: #909399;
            font-weight: 500;
          }

          .source-value {
            color: #303133;
            font-weight: 600;
            background: #f0f9ff;
            padding: 2px 8px;
            border-radius: 4px;
          }
        }

        .type-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        span {
          display: flex;
          align-items: center;
          gap: 5px;
        }
      }

      .club-actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-top: 15px;
        border-top: 1px solid #EBEEF5;
        
        .el-button--info.is-plain {
          color: #ff6b00;
          border-color: #ff6b00;
          background-color: #fff;

          &:hover, &:focus {
            background: #fff9f2;
            border-color: #ff6b00;
            color: #ff6b00;
          }
        }
      }
    }
  }

  .no-results {
    text-align: center;
    padding: 40px;
    color: #909399;

    i {
      font-size: 48px;
      margin-bottom: 10px;
    }

    p {
      font-size: 16px;
    }
  }
}
</style> 